/**
 * Dadan Export Service
 * Handles API calls for exporting stitched videos to Dadan platform
 */

import axios from 'axios';
import logger from '../utils/logger';
import { getAuthToken, getUserId } from '../utils/authUtils';

export interface StartExportRequest {
  projectId: number;
  projectTitle: string;
  options: {
    includeChapters: boolean;
    quality: '720p' | '1080p' | '2k';
  };
}

export interface StartExportResponse {
  success: boolean;
  jobId?: string;
  error?: string;
}

export interface ExportJob {
  id: string;
  projectId: number;
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed';
  progress: number;
  videoId?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExportStatusResponse {
  success: boolean;
  job?: ExportJob;
  error?: string;
}

export interface LatestExportJob extends ExportJob {
  isActive: boolean;
}

export interface LatestExportResponse {
  success: boolean;
  job?: LatestExportJob | null;
  error?: string;
}

const API_BASE = '/api';

export interface ExportStreamHandlers {
  onSnapshot: (job: ExportJob) => void;
  onProgress: (job: Partial<ExportJob>) => void;
  onComplete: (job: Partial<ExportJob>) => void;
  onFailed: (job: Partial<ExportJob>) => void;
  onCancelled: (job: Partial<ExportJob>) => void;
  onError: (error: Event) => void;
}

export class DadanExportService {
  /**
   * Start a new export job
   */
  static async startExport(request: StartExportRequest): Promise<StartExportResponse> {
    try {
      const token = getAuthToken();
      const userId = getUserId();
      
      const response = await axios.post<StartExportResponse>(
        `${API_BASE}/projects/${request.projectId}/export`,
        {
          includeChapters: request.options.includeChapters,
          quality: request.options.quality,
          userId, // Pass user ID directly from /api/user response
          projectTitle: request.projectTitle, // Video title for Dadan Records
        },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to start export:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to start export',
      };
    }
  }

  /**
   * Get export job status
   */
  static async getExportStatus(jobId: string): Promise<ExportStatusResponse> {
    try {
      const token = getAuthToken();
      
      const response = await axios.get<ExportStatusResponse>(
        `${API_BASE}/export/${jobId}/status`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to get export status:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get export status',
      };
    }
  }

  /**
   * Connect to SSE stream for real-time export progress updates
   * Returns the EventSource instance for lifecycle management
   */
  static connectToExportStream(jobId: string, handlers: ExportStreamHandlers): EventSource {
    const url = `${API_BASE}/export/${jobId}/stream`;
    const eventSource = new EventSource(url);

    const parseData = (event: MessageEvent): any => {
      try {
        return JSON.parse(event.data);
      } catch {
        logger.error('[DadanExportService] Failed to parse SSE data:', event.data);
        return null;
      }
    };

    eventSource.addEventListener('snapshot', (event: MessageEvent) => {
      const data = parseData(event);
      if (data) handlers.onSnapshot(data);
    });

    eventSource.addEventListener('progress', (event: MessageEvent) => {
      const data = parseData(event);
      if (data) handlers.onProgress(data);
    });

    eventSource.addEventListener('complete', (event: MessageEvent) => {
      const data = parseData(event);
      if (data) handlers.onComplete(data);
    });

    eventSource.addEventListener('failed', (event: MessageEvent) => {
      const data = parseData(event);
      if (data) handlers.onFailed(data);
    });

    eventSource.addEventListener('cancelled', (event: MessageEvent) => {
      const data = parseData(event);
      if (data) handlers.onCancelled(data);
    });

    eventSource.onerror = (error: Event) => {
      handlers.onError(error);
    };

    return eventSource;
  }

  /**
   * Cancel an export job
   */
  static async cancelExport(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const token = getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/export/${jobId}/cancel`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to cancel export:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to cancel export',
      };
    }
  }

  /**
   * Get the latest export job for a project (for recovering after page refresh)
   */
  static async getLatestExportJob(projectId: number): Promise<LatestExportResponse> {
    try {
      const token = getAuthToken();
      
      const response = await axios.get<LatestExportResponse>(
        `${API_BASE}/projects/${projectId}/export/latest`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to get latest export job:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get latest export job',
      };
    }
  }

  /**
   * Get share link for a video
   */
  static async getShareLink(videoId: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    try {
      const token = getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/video/${videoId}/share`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      return {
        success: true,
        shareUrl: response.data.shareUrl,
      };
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to get share link:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get share link',
      };
    }
  }

  /**
   * Post chapters to Dadan for a video
   * Called after successful upload when "Add Chapters to Video" is enabled
   */
  static async postChapters(
    videoGuid: string,
    chapters: Array<{ chapter_name: string; chapter_description: string; chapter_start_second: number }>
  ): Promise<{ success: boolean; chaptersPath?: string; error?: string }> {
    try {
      const token = getAuthToken();
      const DADAN_BASE_URL = 'https://app.dadan.io';
      
      const response = await axios.post(
        `${DADAN_BASE_URL}/api/video/${videoGuid}/chapters`,
        { chapters },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        logger.info('[DadanExportService] Chapters posted successfully:', response.data.chaptersPath);
        return {
          success: true,
          chaptersPath: response.data.chaptersPath,
        };
      }

      return {
        success: false,
        error: response.data.message || 'Failed to post chapters',
      };
    } catch (error: any) {
      logger.error('[DadanExportService] Failed to post chapters:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to post chapters',
      };
    }
  }
}
